import * as PIXI from 'pixi.js';
import { logger } from '../../utils/logger';
import { CanvasContext } from './types';

/** Window listeners registered during initialization that cleanup must remove. */
export interface CanvasListeners {
  resize: () => void;
  keyDown: (event: KeyboardEvent) => void;
  beforeUnload: () => void;
  /** Observer keeping the renderer sized to its container; disconnected on cleanup. */
  resizeObserver?: ResizeObserver;
}

/**
 * Builds the teardown function that removes window listeners and state
 * subscriptions, clears preview/tooltip/entity references and detaches the
 * ticker callback. The shared global PIXI app is intentionally left alive; it is
 * destroyed on page unload.
 */
export function buildCleanup(
  ctx: CanvasContext,
  onTick: (ticker: PIXI.Ticker) => void,
  listeners: CanvasListeners
): () => void {
  const { refs } = ctx;

  return () => {
    window.removeEventListener('resize', listeners.resize);
    window.removeEventListener('keydown', listeners.keyDown);
    window.removeEventListener('beforeunload', listeners.beforeUnload);
    listeners.resizeObserver?.disconnect();

    if (refs.stateUnsubscribe.current) {
      refs.stateUnsubscribe.current();
      refs.stateUnsubscribe.current = null;
    }

    if (refs.interactionUnsubscribe.current) {
      refs.interactionUnsubscribe.current();
      refs.interactionUnsubscribe.current = null;
    }

    // Clear preview node first (before clearing the viewport reference)
    if (refs.previewNode.current && refs.viewportContainer.current) {
      try {
        refs.viewportContainer.current.removeChild(refs.previewNode.current);
      } catch (error) {
        logger.pixi.warn('Error removing preview node', error);
      }
      refs.previewNode.current = null;
    }

    if (refs.bundlePreview.current && refs.viewportContainer.current) {
      try {
        refs.viewportContainer.current.removeChild(refs.bundlePreview.current);
      } catch (error) {
        logger.pixi.warn('Error removing bundle preview', error);
      }
      refs.bundlePreview.current = null;
    }

    if (refs.tooltip.current) {
      try {
        const app = refs.appManager.current?.getApp();
        if (app && app.stage) {
          app.stage.removeChild(refs.tooltip.current);
        }
      } catch (error) {
        logger.pixi.warn('Error removing tooltip', error);
      }
      refs.tooltip.current = null;
    }

    refs.connectorEntities.current.clear();
    refs.nodeEntities.current.clear();
    refs.wireBundleEntities.current.clear();
    refs.viewportContainer.current = null;
    refs.bundleManager.current = null;
    refs.bundleDragState.current = null;

    if (refs.interactionManager.current) {
      try {
        refs.interactionManager.current.destroy();
      } catch (error) {
        logger.pixi.warn('Error destroying interaction manager', error);
      }
      refs.interactionManager.current = null;
    }

    if (refs.appManager.current) {
      try {
        const app = refs.appManager.current.getApp();
        if (app && app.ticker && refs.tickerCallbackAttached.current) {
          logger.pixi.debug('Removing ticker callback');
          app.ticker.remove(onTick);
          refs.tickerCallbackAttached.current = false;
        }
        // Don't destroy the global app - it is cleaned up on page unload.
      } catch (error) {
        logger.pixi.warn('Error during cleanup', error);
      } finally {
        refs.appManager.current = null;
        refs.tickerCallbackAttached.current = false;
      }
    }

    refs.viewportManager.current = null;
    refs.gridRenderer.current = null;
  };
}
